import type { Condition } from '@cognite/apm-client';
import { makeToast } from '@cognite/cogs-lab';
import { useMetrics } from '@cognite/metrics';
import { LOCIZE_NAMESPACES } from '@infield/features/i18n';
import { useTranslation } from '@infield/features/i18n';
import { METRICS_NAMESPACES } from '@infield/features/metrics';
import { useCurrentUserContext } from '@infield/providers/current-user-provider';
import { useFDMServices } from '@infield/providers/fdm-services';
import { MutationKeys, QueryKeys } from '@infield/utils/queryKeys';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const useConditionUpsert = () => {
  const { conditionsService } = useFDMServices();
  const queryClient = useQueryClient();
  const { t } = useTranslation(LOCIZE_NAMESPACES.conditions);
  const { user } = useCurrentUserContext();
  const metrics = useMetrics(METRICS_NAMESPACES.auditCondition);

  return useMutation(
    async (condition: Condition) => {
      await conditionsService.updateConditions([condition], user!);
      return condition;
    },
    {
      onError: (err) => {
        makeToast({
          type: 'danger',
          body: t(
            'CONDITION_REQUEST_UPDATE_ERROR',
            'Failed to update condition'
          ),
        });
        captureException(err, {
          level: 'error',
          tags: {
            mutationKey: MutationKeys.CONDITIONS_UPDATE,
          },
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.TEMPLATE],
        });
      },
      onSuccess: async () => {
        metrics.track('Update');
      },
      mutationKey: [MutationKeys.CONDITIONS_UPDATE],
    }
  );
};
